import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import WaveMotif from "@/components/WaveMotif";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import AsideImage from "@/components/AsideImage";
import TopImage from "@/components/TopImage";
import RichText from "@/components/RichText";
import BodyText from "@/components/BodyText";
import WeeklyReflection from "@/components/WeeklyReflection";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

const experiences = [
  {
    to: "/four-levels",
    name: "The Four Levels Experience",
    tagline: "A simple introduction to a set of ideas that can change how you see your life.",
    accent: "#4e2957",
  },
  {
    to: "/pattern-reveal",
    name: "Pattern Reveal",
    tagline: "Discover recurring patterns that may be quietly shaping your results.",
    accent: "#1a4c8e",
  },
  {
    to: "/weekly-quantum-edge",
    name: "Weekly Quantum Edge",
    tagline: "A weekly opportunity to reconnect, reflect, and continue growing alongside a thoughtful community.",
    accent: "#2eb7ab",
  },
  {
    to: "/forward-reset",
    name: "Forward Reset",
    tagline: "A private advisory experience for high-responsibility people navigating significant transitions.",
    accent: "#91a33a",
  },
  {
    to: "/daily-quantum-edge",
    name: "Daily Quantum Edge",
    tagline: "An ongoing practice for those who want to intentionally strengthen the way they think, live, and lead.",
    accent: "#eabe1d",
  },
];

export default function Home() {
  const t = useContent("home");
  useSeo("home", {
    title: "Quantum Results — Reconnect. Reflect. Move forward with clarity.",
    description:
      "Quantum Results creates thoughtful experiences that help people slow down, think more clearly, and discover their next meaningful step forward.",
  });
  return (
    <div data-testid="page-home">
      <TopImage src={IMG.home.welcome.src} alt={IMG.home.welcome.alt} credit={IMG.home.welcome.credit} testId="home-top-image" aspect="aspect-[21/8]" />
      {/* 1. Welcome */}
      <section className="relative overflow-hidden" data-testid="home-welcome">
        <WaveMotif className="absolute inset-x-0 top-0 h-[520px] w-full opacity-70 animate-wave-drift" />
        <div className="relative container-narrow pt-16 pb-24 sm:pt-20 sm:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <RichText as="div" className="eyebrow mb-8" value={t("welcome_eyebrow", "Quantum Results")} />
            <h1 className="display text-[44px] sm:text-[60px] lg:text-[72px]">
              Reconnect with yourself.
              <br />
              <span className="text-brand-blue">Move forward</span> with greater clarity,
              confidence, and intention.
            </h1>
            <RichText
              as="p"
              className="prose-qr mt-10 max-w-2xl"
              value={t(
                "welcome_p1",
                "Life doesn't always become difficult because we lack ability. Sometimes we simply lose our connection to ourselves beneath the demands of responsibility, uncertainty, and everyday life."
              )}
            />
            <RichText
              as="p"
              className="prose-qr mt-4 max-w-2xl"
              value={t(
                "welcome_p2",
                "Quantum Results exists to create thoughtful experiences that help people slow down, think more clearly, and discover their next meaningful step forward. Whether you're exploring a new direction, navigating an important transition, or simply sensing that something needs to change, you're welcome here."
              )}
            />
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/#ecosystem"
                data-testid="hero-primary-cta"
                onClick={(e) => {
                  const el = document.getElementById("ecosystem");
                  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors"
              >
                {t("welcome_cta", "Explore the Quantum Results Experiences")}
                <span aria-hidden>↓</span>
              </Link>
              <Link
                to="/about"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 text-[14px] text-brand-ink/80 hover:text-brand-blue transition-colors"
              >
                Learn About Dan Hoover <span aria-hidden>→</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Recognition */}
      <Section
        eyebrow={t("recognition_eyebrow", "Recognition")}
        title={t("recognition_title", "You may be here because…")}
        testId="home-recognition"
      >
        <BodyText
          className=""
          value={t.body(
            "recognition_body",
            `Life looks fine from the outside. But inside, something feels different.

Perhaps you're carrying more responsibility than ever before. Maybe you're navigating a significant transition, facing an important decision, or wondering why the results you want aren't matching the effort you're investing.

You don't necessarily need more information. You may simply need the space to think more clearly.

**If any of this feels familiar, you're not alone. Many thoughtful people arrive here during times when they're looking less for answers… and more for clarity.**`
          )}
        />
      </Section>

      {/* 3. Philosophy */}
      <section className="bg-brand-paper/60 border-y border-brand-rule/60" data-testid="home-philosophy">
        <Section
          eyebrow={t("philosophy_eyebrow", "Philosophy")}
          title={t("philosophy_title", "Better decisions begin with greater clarity.")}
        >
          <BodyText
            className=""
            value={t.body(
              "philosophy_body",
              `At Quantum Results, we believe meaningful improvement begins from the inside out.

When life becomes busy, uncertain, or demanding, it's easy to lose perspective. We react instead of reflect. We solve problems without asking better questions. Greater clarity changes that.

As we reconnect with ourselves, our thinking becomes clearer. Better decisions follow. And those decisions begin creating different results.

That's why every experience at Quantum Results is designed to create space for reflection, awareness, and intentional action.`
            )}
          />
        </Section>
      </section>

      {/* 3b. Weekly Reflection (CMS-refreshable; blank body hides card) */}
      <WeeklyReflection
        eyebrow={t("reflection_eyebrow", "This Week's Reflection")}
        body={
          t.has("reflection_body")
            ? (t.raw("reflection_body") || "")
            : "Sometimes the most meaningful step forward begins with a quiet pause — one that lets us hear ourselves again."
        }
        attribution={t("reflection_attribution", "")}
      />

      {/* 4. Ecosystem */}
      <section id="ecosystem" className="relative overflow-hidden scroll-mt-24" data-testid="home-ecosystem">
        <div className="container-narrow py-24">
          <div className="max-w-3xl">
            <RichText as="div" className="eyebrow mb-5" value={t("ecosystem_eyebrow", "The Quantum Results Ecosystem")} />
            <h2 className="display text-[32px] sm:text-[44px] lg:text-[52px] mb-6">
              One philosophy.
              <br />
              <span className="text-brand-turquoise">Multiple ways</span> to begin.
            </h2>
            <RichText
              as="p"
              className="font-serif italic text-[19px] sm:text-[21px] text-brand-ink/70 leading-relaxed mb-8"
              value={t(
                "ecosystem_anchor",
                "Begin where you are. Trust your path. Grow with intention. Live the life you're here to create."
              )}
            />
            <BodyText
              value={t.body(
                "ecosystem_intro_body",
                `Every person arrives here with a different story. Some are navigating a major transition. Some are looking for greater understanding and clarity. Some want a supportive community to help them continue growing. Others are simply curious about what might be possible.

That's why Quantum Results offers several experiences, each designed to meet you where you are and help you take your next meaningful step. There isn't one right place to begin. There's simply the place that feels right for you.`
              )}
            />
          </div>

          {/* Experience cards */}
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {experiences.map((x, i) => (
              <motion.div
                key={x.to}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`${i === 0 ? "md:col-span-2" : ""}`}
              >
                <Link
                  to={x.to}
                  data-testid={`ecosystem-card-${x.to.replace("/", "")}`}
                  className="group block relative overflow-hidden rounded-lg border border-brand-rule/70 bg-brand-cream p-8 sm:p-10 transition-all duration-500 hover:border-brand-ink/40 hover:shadow-[0_20px_60px_-40px_rgba(26,37,64,0.35)]"
                >
                  <span
                    className="absolute left-0 top-0 h-full w-[3px] transition-all duration-500 group-hover:w-[6px]"
                    style={{ background: x.accent }}
                    aria-hidden
                  />
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div
                        className="text-[11px] uppercase tracking-[0.22em] mb-3"
                        style={{ color: x.accent }}
                      >
                        Experience 0{i + 1}
                      </div>
                      <h3 className="font-serif text-[26px] sm:text-[30px] text-brand-ink leading-tight">
                        {x.name}
                      </h3>
                      <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/70 max-w-xl">
                        {x.tagline}
                      </p>
                    </div>
                    <span className="mt-2 text-brand-ink/40 group-hover:text-brand-ink transition-all group-hover:translate-x-1">
                      →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12">
            <Link
              to="/four-levels"
              data-testid="ecosystem-explore-cta"
              className="inline-flex items-center gap-2 text-[14px] text-brand-blue hover:text-brand-ink transition-colors"
            >
              Explore the Possibilities <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Meet Dan Hoover */}
      <section className="bg-brand-paper/60 border-y border-brand-rule/60" data-testid="home-meet-dan">
        <div className="container-narrow py-20 sm:py-24 grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-center">
          <div className="max-w-2xl">
            <RichText as="div" className="eyebrow mb-5" value={t("meetdan_eyebrow", "Meet Dan Hoover")} />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] lg:text-[46px] mb-8"
              value={t("meetdan_title", "Three decades. One question. Countless conversations.")}
            />
            <BodyText
              value={t.body(
                "meetdan_body",
                `For more than three decades, Dan Hoover has helped people navigate change, make better decisions, and move forward with greater confidence.

His work combines executive leadership experience, thoughtful coaching, and a lifelong curiosity about how people grow, adapt, and create meaningful change.

Today, through Quantum Results, Dan creates experiences that help people reconnect with themselves, gain greater clarity, and intentionally move toward the lives they want to create.

Whether speaking to a group or working one-on-one, his approach remains the same: create a space where clearer thinking becomes possible.`
              )}
            />
            <div className="mt-6">
              <Link to="/about" data-testid="meet-dan-cta" className="link-quiet text-[15px]">
                Learn More About Dan →
              </Link>
            </div>
          </div>
          <AsideImage
            src={IMG.home.meetDan.src}
            alt={IMG.home.meetDan.alt}
            aspect="aspect-[4/5]"
            testId="home-meet-dan-image"
          />
        </div>
      </section>

      {/* 6. Continue the Conversation (Resources teaser) */}
      <section className="container-narrow py-24" data-testid="home-resources-teaser">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center">
          <AsideImage
            src={IMG.home.resourcesTeaser.src}
            alt={IMG.home.resourcesTeaser.alt}
            aspect="aspect-[5/4]"
            testId="home-resources-image"
          />
          <div className="max-w-xl">
            <RichText as="div" className="eyebrow mb-5" value={t("resources_eyebrow", "Continue the Conversation")} />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] lg:text-[46px] mb-8"
              value={t(
                "resources_title",
                "Sometimes one new idea is enough to change the direction of your thinking."
              )}
            />
            <BodyText
              value={t.body(
                "resources_body",
                `Explore articles, reflections, videos, and other resources designed to help you think more clearly, see new possibilities, and continue moving forward at your own pace.

Whether you're simply curious or intentionally exploring what's next, you're always welcome here.`
              )}
            />
            <div className="mt-6">
              <Link to="/resources" data-testid="resources-teaser-cta" className="link-quiet text-[15px]">
                Explore the Resources →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Wherever you are */}
      <section className="relative overflow-hidden bg-brand-paper/40 border-t border-brand-rule/60" data-testid="home-wherever-you-are">
        <div className="relative container-narrow py-24 grid gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-center">
          <div className="max-w-2xl">
            <RichText as="div" className="eyebrow mb-5" value={t("wherever_eyebrow", "Wherever you are, you're welcome here.")} />
            <RichText
              as="h2"
              className="display text-[32px] sm:text-[44px] lg:text-[52px] mb-8"
              value={t("wherever_title", "Every meaningful journey begins somewhere.")}
            />
            <BodyText
              value={t.body(
                "wherever_body",
                `Whether you're simply exploring new ideas, looking for greater clarity, or ready for a deeper conversation, we'd be honored to be part of your journey.

Take your time. Explore what resonates.

And when the time feels right, we'd love to hear from you.`
              )}
            />
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="wherever-primary-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors"
              >
                Start a Conversation
              </Link>
              <Link
                to="/contact"
                data-testid="wherever-secondary-cta"
                className="text-[14px] text-brand-ink/70 hover:text-brand-blue transition-colors"
              >
                Contact Quantum Results →
              </Link>
            </div>
          </div>
          <AsideImage
            src={IMG.home.wherever.src}
            alt={IMG.home.wherever.alt}
            aspect="aspect-[4/5]"
            testId="home-wherever-image"
          />
        </div>
      </section>
    </div>
  );
}
